import * as React from 'react'

import Fade from '@mui/material/Fade'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'

import { LeadAssignmentRuleInfoHeader } from './LeadAssignmentRuleInfoHeader'
import type { LeadAssignmentRule } from '../LeadAssignmentRuleForm/typings'
import { LeadAssignmentConditionInfo } from './LeadAssignmentConditionInfo'
import { useGetAssignmentRuleConditions } from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleList/hooks/useGetAssignmentRuleConditions'
import { useTheme } from '@mui/material/styles'
import { useGetBranchList } from '@user/store/branches'
import { useGetLocationTypesOnMount } from '@user/store/locationTypes'
import { useGetCampaignsOnMount } from '@user/store/campaigns'
import { useGetDivisionsOnMount } from '@user/store/divisions'
import { useGetSourcesOnMount } from '@user/store/sources'
import { useGetUserListOnMount } from '@user/store/users'
import { useGetStatesOnMount } from '@user/store/dictionaries'
import { useGetTargetPestsOnMount } from '@user/store/targetPests'
import { useGetStatesProvinceOnMount } from '../../ImportUtility/hooks/useGetStatesProvinceOnMount'

type Props = {
  open: boolean
  onClose: () => void
  popoverId: string
  anchorEl: Element
  rule: LeadAssignmentRule
}

const transformOrigin = { vertical: -25, horizontal: 65 }

export function LeadAssignmentRuleInfoPopover(props: Props) {
  const { open, onClose, popoverId, anchorEl, rule } = props

  // Only needed to render the details, so load them when the popover is first opened.
  useGetBranchList(true)
  useGetLocationTypesOnMount()
  useGetCampaignsOnMount()
  useGetDivisionsOnMount()
  useGetSourcesOnMount()
  useGetUserListOnMount()
  useGetStatesOnMount()
  useGetTargetPestsOnMount()
  useGetStatesProvinceOnMount()

  const getAssignmentRuleConditions = useGetAssignmentRuleConditions()

  const conditionValues = getAssignmentRuleConditions(
    rule.assignmentRuleConditions || [],
    'all',
  )
  const theme = useTheme()
  return (
    <Popover
      id={popoverId}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={transformOrigin}
      elevation={3}
      TransitionComponent={Fade}
      title="Assignment Rule Details"
      data-test-id="lead-assignment-rule-info-popover"
    >
      <Stack padding={2} width={420}>
        <LeadAssignmentRuleInfoHeader onClose={onClose} />
        <Stack marginTop={1.2} marginBottom={1.2}>
          <Typography variant="subtitle2" color={theme.palette.text.primary}>
            Rule Applied
          </Typography>
          <Typography variant="subtitle1" color={theme.palette.text.primary}>
            {rule.name}
          </Typography>
        </Stack>
        <Divider />
        <Stack gap={1} marginTop={1.2}>
          {conditionValues.map((condition, idx) => (
            <LeadAssignmentConditionInfo
              condition={condition}
              key={`condition_${idx}`}
            />
          ))}
        </Stack>
      </Stack>
    </Popover>
  )
}
